/** ASCE load values returned from the ratings API. */
export interface AsceLoadResult {
  windLoadMph: number;
  snowLoadPsf: number;
}

/** Order-level load ratings used when persisting or displaying building values. */
export interface OrderLoadContext {
  windLoad: number;
  snowLoad: number;
  manualLoadRatingCertified?: boolean;
}

export interface BuildingAsceFields {
  asceWindLoadMph: number;
  asceSnowLoadPsf: number;
  asceRiskLevel: number;
}

export function asceMatchesOrder(asceResult: AsceLoadResult, order: OrderLoadContext): boolean {
  return asceResult.windLoadMph === order.windLoad && asceResult.snowLoadPsf === order.snowLoad;
}

/** Whether the user should be prompted to update order-level load ratings. */
export function shouldPromptOrderAsceUpdate(
  asceResult: AsceLoadResult,
  order: OrderLoadContext
): boolean {
  if (order.manualLoadRatingCertified) {
    return false;
  }
  return !asceMatchesOrder(asceResult, order);
}

/**
 * ASCE fields to persist on a building.
 * Order-level ratings (including manual override) stay authoritative until the user confirms an update.
 */
export function getBuildingAsceFieldsForSave(
  asceResult: AsceLoadResult,
  order: OrderLoadContext,
  riskLevel: number
): BuildingAsceFields {
  if (order.manualLoadRatingCertified || !asceMatchesOrder(asceResult, order)) {
    return {
      asceWindLoadMph: order.windLoad,
      asceSnowLoadPsf: order.snowLoad,
      asceRiskLevel: riskLevel,
    };
  }
  return {
    asceWindLoadMph: asceResult.windLoadMph,
    asceSnowLoadPsf: asceResult.snowLoadPsf,
    asceRiskLevel: riskLevel,
  };
}

/** Order-level ASCE fields when manual override makes the API-derived values irrelevant. */
export function getOrderLevelBuildingAsceFields(
  order: OrderLoadContext,
  riskLevel: number
): BuildingAsceFields {
  return {
    asceWindLoadMph: order.windLoad,
    asceSnowLoadPsf: order.snowLoad,
    asceRiskLevel: riskLevel,
  };
}

/** Wind/snow shown in Manage Building — always matches order-level ratings used for truss specs. */
export function getBuildingDisplayLoadRatings(
  building: { asceWindLoadMph?: number; asceSnowLoadPsf?: number },
  order: OrderLoadContext
): { windLoadMph: number; snowLoadPsf: number } {
  if (order.manualLoadRatingCertified) {
    return { windLoadMph: order.windLoad, snowLoadPsf: order.snowLoad };
  }

  if (
    building.asceWindLoadMph != null &&
    building.asceSnowLoadPsf != null &&
    building.asceWindLoadMph === order.windLoad &&
    building.asceSnowLoadPsf === order.snowLoad
  ) {
    return {
      windLoadMph: building.asceWindLoadMph,
      snowLoadPsf: building.asceSnowLoadPsf,
    };
  }

  return { windLoadMph: order.windLoad, snowLoadPsf: order.snowLoad };
}
